import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setShowMapView } from 'store/actions';
import './styles.scss';

import CloseIcon from 'assets/icons/CloseIcon';
import MapMarkers from 'assets/images/map/MapMarkers';
import { useHistory, useLocation } from 'react-router-dom';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import MAP_IMAGE from '../../assets/images/map/map2.webp';

const isMobile = window.innerWidth < 768;
const ETOWN_CENTRAL_1 = '/assets/images/etown-central-1.jpg';
const ETOWN_CENTRAL_2 = '/assets/images/etown-central-2.jpg';
const ETOWN_CENTRAL_3 = '/assets/images/etown-central-3.jpg';
const ETOWN_CENTRAL_4 = '/assets/images/etown-central-4.jpg';
const ETOWN_CENTRAL_5 = '/assets/images/etown-central-5.jpg';

const wh_ratio = window.innerWidth / window.innerHeight;
const isBigHeightTablet = wh_ratio > 0.65 && wh_ratio < 1;
let mapZoom = isBigHeightTablet ? 1 * window.innerHeight/(window.innerWidth/2700*1560) : 1.77;

const MapComponent = ({ imgUrl = MAP_IMAGE }) => {
  const dispatch = useDispatch();
  const mapRef = useRef(null);
  const [mapElements, setMapElements] = useState([]);
  const [isPlacingElement, setIsPlacingElement] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  const history = useHistory();
  const location = useLocation();
  // Lấy map image từ tour hoặc sử dụng default
  const mapImageUrl = imgUrl;

  // Slide images for etown-central
  const slideImages = [
    ETOWN_CENTRAL_1,
    ETOWN_CENTRAL_2,
    ETOWN_CENTRAL_3,
    ETOWN_CENTRAL_4,
    ETOWN_CENTRAL_5,
  ];

  const isEtownCentral = location.pathname.includes('/etown-central');

  const handlePrevSlide = () => {
    setCurrentSlideIndex((prev) =>
      prev === 0 ? slideImages.length - 1 : prev - 1
    );
  };

  const handleNextSlide = () => {
    setCurrentSlideIndex((prev) =>
      prev === slideImages.length - 1 ? 0 : prev + 1
    );
  };

  const handleMapClick = useCallback(
    (e) => {
      if (!isPlacingElement) return;

      const rect = mapRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Tính toán tọa độ theo phần trăm (0-1)
      const xPercent = x / rect.width;
      const yPercent = y / rect.height;

      // Tạo element mới
      const newElement = {
        id: `element-${Date.now()}`,
        coordinates: [xPercent, yPercent],
        name: `Điểm ${mapElements.length + 1}`,
        sceneId: null, // Sẽ được gán khi click vào element
      };

      setMapElements([...mapElements, newElement]);
      setIsPlacingElement(false);
    },
    [isPlacingElement, mapElements]
  );

  const handleElementClick = useCallback(
    (element, e) => {
      e.stopPropagation();
      if (element === 'red') {
        history.push(`/front-view`);
      }
      return;
    },
    [history]
  );

  const handleClose = useCallback(() => {
    dispatch(setShowMapView(false));
  }, [dispatch]);

  const handleAddElement = useCallback(() => {
    setIsPlacingElement(true);
  }, []);

  const handleCancelPlacement = useCallback(() => {
    setIsPlacingElement(false);
  }, []);

  if (!mapImageUrl) {
    return (
      <div className="map-component">
        <div className="map-component__loading">
          <p>Đang tải bản đồ...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="drag-image-overlay">
      {isEtownCentral && (
        <div className="map-component-close-button">
          <button onClick={() => history.push('/map')}>
            <CloseIcon width={20} height={20} color="#fff" />
          </button>
        </div>
      )}
      {/* Slide Navigation Buttons - Outside TransformWrapper */}
      {isEtownCentral && (
        <>
          <button
            className="map-slide-nav map-slide-nav--prev"
            onClick={handlePrevSlide}
            aria-label="Previous slide"
          >
            <svg fill="#ffffff" width="20px" height="20px" viewBox="-1.92 -1.92 35.84 35.84" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff" stroke-width="2.56" transform="matrix(-1, 0, 0, 1, 0, 0)"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.064"></g><g id="SVGRepo_iconCarrier"> <path d="M8.489 31.975c-0.271 0-0.549-0.107-0.757-0.316-0.417-0.417-0.417-1.098 0-1.515l14.258-14.264-14.050-14.050c-0.417-0.417-0.417-1.098 0-1.515s1.098-0.417 1.515 0l14.807 14.807c0.417 0.417 0.417 1.098 0 1.515l-15.015 15.022c-0.208 0.208-0.486 0.316-0.757 0.316z"></path> </g></svg>
          </button>
          <button
            className="map-slide-nav map-slide-nav--next"
            onClick={handleNextSlide}
            aria-label="Next slide"
          >
            <svg fill="#ffffff" width="20px" height="20px" viewBox="-1.92 -1.92 35.84 35.84" version="1.1" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff" stroke-width="2.56"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" stroke="#CCCCCC" stroke-width="0.064"></g><g id="SVGRepo_iconCarrier"> <path d="M8.489 31.975c-0.271 0-0.549-0.107-0.757-0.316-0.417-0.417-0.417-1.098 0-1.515l14.258-14.264-14.050-14.050c-0.417-0.417-0.417-1.098 0-1.515s1.098-0.417 1.515 0l14.807 14.807c0.417 0.417 0.417 1.098 0 1.515l-15.015 15.022c-0.208 0.208-0.486 0.316-0.757 0.316z"></path> </g></svg>
          </button>
          <div className="map-slide-indicator">
            {currentSlideIndex + 1} / {slideImages.length}
          </div>
        </>
      )}
      <TransformWrapper
        key={`transform-${isEtownCentral ? 'etown-central' : 'default'}`}
        disablePadding
        wheel={{ step: 0.05 }}
        centerOnInit={false}
        limitToBounds={true}
        initialScale={isEtownCentral ? 1 : (isMobile ? 4 : mapZoom)}
        initialPositionX={isEtownCentral ? 0 : (isMobile ? -(window.innerWidth * 1.55) : (window.innerWidth * mapZoom * 0.66 - window.innerWidth * 0.5) * -1)}
        initialPositionY={isEtownCentral ? 0 : (isMobile ? 0 : isBigHeightTablet ? 0: (mapZoom - 1) * window.innerHeight * 0.75 * -1)}
        minScale={ (isMobile ? 1.5 : 1)}
        maxScale={8}
      >
        {() => (
          <>
            <TransformComponent
              wrapperClass="drag-image-overlay__viewport"
              contentClass="drag-image-overlay__content"
            >
              <div
                className="map-component"
                style={{
                  height: `${isEtownCentral ? '100dvh' : 'unset'}`,
                  width: `${isEtownCentral ? '100dvw' : 'unset'}`,
                }}
              >
                <div className="map-component__header">
                  <h2>Bản đồ</h2>
                  <div className="map-component__actions">
                    {!isPlacingElement ? (
                      <button
                        className="map-component__btn map-component__btn--add"
                        onClick={handleAddElement}
                      >
                        Thêm điểm
                      </button>
                    ) : (
                      <button
                        className="map-component__btn map-component__btn--cancel"
                        onClick={handleCancelPlacement}
                      >
                        Hủy
                      </button>
                    )}
                    <button
                      className="map-component__btn map-component__btn--close"
                      onClick={handleClose}
                    >
                      Đóng
                    </button>
                  </div>
                </div>

                <div
                  ref={mapRef}
                  className={`map-component__container ${
                    isPlacingElement ? 'placing' : ''
                  } ${isEtownCentral ? 'slide-mode' : ''}`}
                  onClick={handleMapClick}
                >
                  {isEtownCentral ? (
                    <div className="map-slide-wrapper">
                      {slideImages.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Slide ${index + 1}`}
                          className={`map-component__slide-image ${
                            index === currentSlideIndex ? 'active' : ''
                          }`}
                        />
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        lineHeight: 0,
                        maxWidth: '100%',
                        maxHeight: '100%',
                      }}
                    >
                      <img
                        src={mapImageUrl}
                        alt="Map"
                        className="map-component__image"
                        style={{
                          display: 'block',
                          width: 'auto',
                          height: 'auto',
                        }}
                      />

                      {location.pathname.includes('/map') && (
                        <MapMarkers handleElementClick={handleElementClick} />
                      )}
                    </div>
                  )}
                </div>

                {isPlacingElement && (
                  <div className="map-component__hint">
                    <p>Nhấp vào bản đồ để đặt điểm mới</p>
                  </div>
                )}
              </div>
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default MapComponent;
